import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  FormControl,
  Stack,
  TextField,
  MenuItem,
  Button,
  Typography,
  Checkbox,
} from '@mui/material';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { makeStyles } from 'tss-react/mui';
import {
  useForm,
  Controller,
  FormProvider,
  SubmitHandler,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import { yupResolver } from '@hookform/resolvers/yup';
import { HttpStatusCode, isAxiosError } from 'axios';
import { useConfirm } from 'components/ConfirmDialog';
import { MapProvider } from 'shared/map-exports';
import { Map } from 'features/mapping/components/Map';
import { MapType } from 'features/mapping/mapTypePolicy';
import { EvacZone, EvacZoneStatus } from 'shared/types';
import { useDefaultMapCenter } from 'hooks/useDefaultMapCenter';
import { useLatLngFromQueryParams } from 'hooks/useLatLngFromQueryParams';
import { useInitialMapZoom } from 'hooks/useInitialMapZoom';
import { useQueryParams } from 'hooks/useQueryParams';
import { getEvacZoneStyleFromGeoEvent, removeHTMLTags } from 'shared/utils';
import { useUserRegionsQuery } from 'hooks/useUserRegionsQuery';
import { useFloodingGeoEvent } from 'features/flooding/hooks/useFloodingGeoEvent';
import { NotificationType } from 'features/notifications/notifications.types';
import { NotificationType as NotificationTypeConstant } from 'features/notifications/notifications.constants';
import {
  getMessageFromGeoEventSaveErrorResponse,
  getRedundancyMessage,
  isGeoEventUpgradedFromSilent,
} from 'features/geoEvents/geoEvent.utils';
import { useRegionsChangeHandler } from 'features/geoEvents/hooks/useRegionsChangeHandler';
import { FormWithoutEnter } from 'components/FormWithoutEnter';
import { FormInternalLinks, FormLinks } from 'components/FormLinks';
import { InternalNotesInput } from 'components/InternalNotesInput';
import { EvacuationFormSection } from 'features/evacZones/components/EvacuationFormSection';
import { useGeoEventEvacZones } from 'features/evacZones/hooks/useGeoEventEvacZones';
import { useSyncInitialLocationToFormState } from 'features/geoEvents/hooks/useSyncInitialLocationToFormState';
import { useAllGeoEvents } from 'features/geoEvents/hooks/useAllGeoEvents';
import {
  ActiveFloodingsLayer,
  InactiveFloodingsLayer,
} from 'features/mapping/components/layers/FloodingsLayer';
import { WildfiresLayer } from 'features/mapping/components/layers/WildfiresLayer';
import { FormExternalFeatureLayers } from 'features/mapping/components/layers/FormExternalFeatureLayers';
import { useFormBlocker } from 'hooks/useFormBlocker';
import { useGeoEventEditMarker } from 'features/geoEvents/hooks/useGeoEventEditMarker/useGeoEventEditMarker';
import { useMarkerEditLock } from 'features/geoEvents/hooks/useMarkerEditLock';
import { useMarkerEditRegionSync } from 'features/geoEvents/hooks/useMarkerEditRegionSync';
import { useMarkerMoveConfirmation } from 'features/geoEvents/hooks/useMarkerMoveConfirmation';
import {
  getFloodingEventIcon,
  getFloodingEventScale,
} from 'features/mapping/icons';
import { iconFootprint } from 'features/mapping/components/MarkerEditConnectorLine';
import { MarkerEditControls } from 'features/mapping/components/MarkerEditControls';
import { MarkerEditGhost } from 'features/mapping/components/MarkerEditGhost';
import { MediaForm, isMediaAlertCamera } from 'components/MediaForm';
import { CriticalAlertConfirmation } from 'features/reports/components/CriticalAlertConfirmation';
import { InitialReportNotificationTypeField } from 'features/reports/components/InitialReportNotificationTypeField';
import { ReportMessageField } from 'features/reports/components/ReportMessageField';
import { useCanSendCriticalInitialReport } from 'features/geoEvents/hooks/useCanSendCriticalInitialReport';
import { PushNotificationPreview } from 'features/notifications/components/PushNotificationPreview';
import { getGeoEventRegionsChanged } from 'features/geoEvents/hooks/useGeoEventRegionsChanged';
import { useInitialGeoEventReport } from 'features/reports/hooks/useInitialGeoEventReport';
import { getGeoEventNotificationPreviewState } from 'features/geoEvents/utils/getGeoEventNotificationPreviewState';
import {
  ImageUploadData,
  useImageUpload,
} from 'features/reports/hooks/useImageUpload';
import { Flooding, RelatedFlooding } from 'features/flooding/flooding.types';
import { useGeoEventAutocomplete } from 'features/geoEvents/hooks/useGeoEventAutocomplete';
import { UrgencySelector } from 'features/geoEvents/components/UrgencySelector';
import { GeoEventFormTabs } from 'features/geoEvents/components/GeoEventFormTabs';
import { InternalFormSection } from 'features/geoEvents/components/InternalFormSection';
import { useResetOnInitialValuesChange } from 'features/geoEvents/hooks/useResetOnInitialValuesChange';
import { getIncidentRemountKey } from 'features/geoEvents/utils/getIncidentRemountKey';
import { useAuthState } from 'state/useAuthState';
import { MapFormLayout } from 'components/MapFormLayout';
import { useIosKeyboardScroller } from 'hooks/useIosKeyboardScroller';
import { useReleaseFlag } from 'hooks/useReleaseFlags';
import { FloodingManualDeactivationField } from 'features/flooding/components/FloodingManualDeactivationField';
import { FloodingDeactivationAlert } from 'features/flooding/components/FloodingDeactivationAlert';
import { AlertCamerasLayer } from 'features/mapping/components/layers/AlertCamerasLayer';
import { PendingEvacZonesError } from 'features/evacZones/components/PendingEvacZonesError';
import { PendingReviewEvacZoneErrorType } from 'features/geoEvents/geoEvent.types';
import { handlePendingReviewZonesErrorOrFallback } from 'features/geoEvents/utils/handlePendingReviewZonesErrorOrFallback';
import { useQueryClient } from '@tanstack/react-query';
import { AutocompleteWithBackdrop } from 'components/AutocompleteWithBackdrop';
import { GeoEventTypes, EvacZoneStatuses } from '../../../../constants';
import { FloodingFormProps, FloodingFormValues } from './FloodingForm.types';
import {
  floodingFormValuesToServerValues,
  getSubmitButtonKey,
  getValidationSchema,
  getDefaultValues,
  validateAreaImpacted,
} from './FloodingForm.utils';
import { FloodingChangelog } from '../FloodingChangelog';
import { FloodingWarnings } from '../FloodingWarnings';

const preserveFieldsOnReset: (keyof FloodingFormValues)[] = [
  'media',
  'confirmSelectedZones',
  'confirmAdditionalRegions',
  'confirmRemovedRegions',
];

const useStyles = makeStyles()((theme) => ({
  lastItem: {
    flex: 1,
    display: 'flex',
    alignItems: 'flex-end',
  },
  noPushNotif: {
    fontWeight: theme.typography.fontWeightMedium,
    textAlign: 'center',
  },
  capitalizeLabel: {
    textTransform: 'capitalize',
  },
}));

type FloodingMarkerProps = {
  geoEvent: Partial<Flooding>;
  onChange: (location: { lat: number; lng: number }) => void;
  locked?: boolean;
  moveOnEvacZoneClick?: boolean;
};

const FloodingMarker = (props: FloodingMarkerProps): JSX.Element | null => {
  const {
    geoEvent,
    onChange,
    locked = false,
    moveOnEvacZoneClick = false,
  } = props;
  const { markerProps } = useGeoEventEditMarker({
    geoEventType: GeoEventTypes.flooding,
    onChange,
    disabled: locked,
    moveOnEvacZoneClick,
  });

  return (
    <Map.FloodingGeoEventMarker
      {...markerProps}
      geoEvent={geoEvent}
      draggable={!locked}
      anchor="bottom"
      isFadable
    />
  );
};

export const FloodingForm = (props: FloodingFormProps): JSX.Element => {
  const { flooding } = props;
  const { classes } = useStyles();
  const { t } = useTranslation();
  const {
    permissions: { isFloodStaffReporter },
  } = useAuthState();
  const { lat: initialLatitude, lng: initialLongitude } =
    useLatLngFromQueryParams();
  const initialZoom = useInitialMapZoom();
  const [{ alertCameraId }] = useQueryParams<{ alertCameraId?: string }>();
  const { wildfireEvents, floodingEvents, hazards } = useAllGeoEvents();
  const queryClient = useQueryClient();
  const confirm = useConfirm();

  const { saveGeoEvent } = useFloodingGeoEvent({ id: flooding?.id });
  const isInternalTabEnabled = useReleaseFlag('internal_tab_on_incidents');

  const { regions: regionOptions, isLoading: isLoadingRegions } =
    useUserRegionsQuery();

  const [isCriticalConfirmed, setIsCriticalConfirmed] = useState(false);

  // Circular dependency workaround: validation schema needs evac zones for form initialization,
  // but evac zones depend on form's regions field. Start with empty array, update via useEffect.
  const [validationRegionEvacZones, setValidationRegionEvacZones] = useState<
    EvacZone[]
  >([]);

  const scrollContainerRef = useRef<HTMLDivElement | null>(null);
  useIosKeyboardScroller(scrollContainerRef);

  const defaultMapCenter = useDefaultMapCenter({
    geoEvent: flooding,
    initialLat: initialLatitude,
    initialLng: initialLongitude,
  });

  const defaultValues = useMemo(
    () =>
      getDefaultValues({
        flooding,
        latitude: initialLatitude,
        longitude: initialLongitude,
        initialAlertCameraId: alertCameraId,
      }),
    [flooding, initialLatitude, initialLongitude, alertCameraId],
  );

  const formMethods = useForm<FloodingFormValues>({
    resolver: yupResolver(
      getValidationSchema(t, validationRegionEvacZones, flooding),
    ),
    defaultValues,
    mode: 'onBlur',
  });

  const {
    control,
    handleSubmit,
    formState,
    watch,
    setValue,
    getValues,
    reset,
  } = formMethods;

  useResetOnInitialValuesChange({
    values: defaultValues,
    getValues,
    reset,
    preserveFields: preserveFieldsOnReset,
  });

  const { unblockedNavigate } = useFormBlocker({
    shouldBlockNavigation: Boolean(formState.isDirty),
  });

  const [
    status,
    lat,
    lng,
    regions,
    activeEvacuations,
    evacZoneStatuses,
    temporarilyDisplayEvacZones,
    unstructuredEvacuationRegionIds,
    releasePendingZoneUids,
    evacuationOrders,
    evacuationWarnings,
    evacuationAdvisories,
    evacuationShelterInPlace,
    customOrders,
    customWarnings,
    customAdvisories,
    customShelterInPlace,
    media,
    fileType,
    reportMessage,
    name,
    notificationType,
    areaImpacted,
    childFloodings,
    parentFlooding,
    reportNotificationType,
  ] = watch([
    'status',
    'lat',
    'lng',
    'regions',
    'activeEvacuations',
    'evacZoneStatuses',
    'temporarilyDisplayEvacZones',
    'unstructuredEvacuationRegionIds',
    'releasePendingZoneUids',
    'evacuationOrders',
    'evacuationWarnings',
    'evacuationAdvisories',
    'evacuationShelterInPlace',
    'customOrders',
    'customWarnings',
    'customAdvisories',
    'customShelterInPlace',
    'media',
    'fileType',
    'reportMessage',
    'name',
    'notificationType',
    'areaImpacted',
    'childFloodings',
    'parentFlooding',
    'reportNotificationType',
  ]);

  const canSendCriticalInitialReport = useCanSendCriticalInitialReport({
    isNewIncident: !flooding,
    isModeratedSubmission: false,
    notificationType,
    isParent: childFloodings.length > 0,
    reporterManaged: true,
  });
  const isCriticalInitialReport =
    canSendCriticalInitialReport &&
    reportNotificationType === NotificationType.Critical;

  const {
    regionEvacZones,
    evacZoneStyles,
    isLoadingEvacZones,
    isErrorEvacZones,
    lockEvacuationZonesChangedNotification,
    unlockEvacuationZonesChangedNotification,
    renderMapLayers,
  } = useGeoEventEvacZones({
    regions,
    evacZoneStatuses,
    geoEvent: flooding,
    setValue,
    releasePendingZoneUids,
    temporarilyDisplayEvacZones,
    forceRegionEvacZonesFetch: true,
  });

  useEffect(() => {
    // Update the validation region evac zones when regions change
    setValidationRegionEvacZones(regionEvacZones);
  }, [regionEvacZones]);

  const hasStructuredEvacZones = Boolean(regionEvacZones?.length > 0);

  // Same baseline as the changelog, so the "confirm added regions" checkbox and
  // the preview always appear together.
  const { regionsAdded } = useMemo(
    () => getGeoEventRegionsChanged(flooding?.regions ?? [], regions),
    [flooding?.regions, regions],
  );

  const { muiAutocompleteProps: geoEventAutocompleteProps } =
    useGeoEventAutocomplete<RelatedFlooding>({
      enabled: Boolean(flooding?.data?.isParent) && isFloodStaffReporter,
      filterGeoEvents: (geoEvents) =>
        geoEvents.filter((ge) => !ge.data.isParent && ge.id !== flooding?.id),
      latLng: { lat, lng },
      value: childFloodings,
      geoEventTypes: ['flooding'],
    });

  // Show parent field when: not a parent incident (parents can't have parents)
  const showParentField = !flooding?.data?.isParent;

  const { muiAutocompleteProps: parentAutocompleteProps } =
    useGeoEventAutocomplete<RelatedFlooding>({
      enabled: showParentField && isFloodStaffReporter,
      filterGeoEvents: (geoEvents) =>
        geoEvents.filter((ge) => ge.id !== flooding?.id),
      latLng: { lat, lng },
      value: parentFlooding,
      geoEventTypes: ['flooding'],
      multiple: false,
    });

  const {
    initialReport,
    isLoading: isLoadingInitialReport,
    isError: isErrorInitialReport,
  } = useInitialGeoEventReport({
    geoEventId: flooding?.id,
    // SYNC WARNING: mirrors `notify_geo_event_updated` on the backend, which
    // pushes the initial report's message both when regions are added and
    // when an incident is upgraded from silent. Fetch in both cases so the
    // preview has a body ready.
    enabled:
      regionsAdded ||
      isGeoEventUpgradedFromSilent({
        previousNotificationType: flooding?.notificationType,
        currentNotificationType: notificationType,
      }),
  });

  // SYNC WARNING: mirrors `create_fcm_message` on the backend, where a flooding's
  // push body is its initial report's message. On create that is the report being
  // submitted; on edit it is the one fetched above. Changing either side without
  // the other makes this preview disagree with what users receive.
  const previewBody = removeHTMLTags(
    flooding ? (initialReport?.message ?? '') : reportMessage,
  );

  const { showPreviewSection, showPreview, showNoNotificationNote } =
    getGeoEventNotificationPreviewState({
      geoEvent: flooding,
      currentNotificationType: notificationType,
      regionsAdded,
      title: name,
      body: previewBody,
      isDirty: formState.isDirty,
      sendsNotifications: true,
    });

  const imgData = useMemo((): ImageUploadData | null => {
    if (!media || !('type' in media) || !fileType) return null;
    return { file: media, fileType };
  }, [fileType, media]);

  const handleImageUploadError = useCallback(() => {
    enqueueSnackbar(t('addIncidentReport.imageUploadError'), {
      variant: 'error',
    });
  }, [t]);

  const {
    uploadImgPromise,
    uploadImageVerification,
    mediaUrl,
    error: uploadImgError,
  } = useImageUpload({
    imgData,
    onError: handleImageUploadError,
    enabled: !flooding,
  });

  const { handleLocationChange } = useSyncInitialLocationToFormState({
    isLoadingRegions,
    regionOptions,
    geoEventType: GeoEventTypes.flooding,
    geoEvent: flooding,
    canUpdateAddressAndRegion: !flooding,
    setValue,
  });

  const onSubmit: SubmitHandler<FloodingFormValues> = async (
    values,
  ): Promise<void> => {
    if (uploadImgPromise) {
      const uploadResult = await uploadImgPromise;
      if (uploadResult instanceof Error) {
        return;
      }

      //  Verify that the upload actually happened
      if (uploadImageVerification) {
        const imageIsUploaded = await uploadImageVerification();
        if (!imageIsUploaded) {
          handleImageUploadError();
          return;
        }
      }
    }

    const data = floodingFormValuesToServerValues(
      values,
      mediaUrl,
      hasStructuredEvacZones,
      isCriticalInitialReport,
    );

    let mutatedGeoEvent = null;
    try {
      lockEvacuationZonesChangedNotification();
      mutatedGeoEvent = await saveGeoEvent({
        updatedGeoEvent: data,
        originalGeoEvent: flooding,
        shouldRetryOnDuplicate: async (error): Promise<boolean> => {
          const resp = await confirm(getRedundancyMessage(error.response));
          return resp;
        },
      });
    } catch (error: unknown) {
      unlockEvacuationZonesChangedNotification();
      if (!isAxiosError(error)) {
        throw error;
      }

      const errorMessage = getMessageFromGeoEventSaveErrorResponse(error, t, {
        defaultMessage: t('createEditFlooding.error.save'),
      });
      const errorStatus = error.response?.status;

      if (errorStatus === HttpStatusCode.BadRequest) {
        await handlePendingReviewZonesErrorOrFallback({
          error,
          queryClient,
          t,
          currentZonesWithStatus: values.evacZoneStatuses,
          updateFormZones: (zonesWithStatus) =>
            setValue('evacZoneStatuses', zonesWithStatus),
          geoEventId: flooding?.id,
          fallbackAction: () =>
            enqueueSnackbar(errorMessage, { variant: 'error' }),
        });

        return;
      }

      enqueueSnackbar(errorMessage, { variant: 'error' });
      return;
    }

    if (!mutatedGeoEvent) {
      // A reporter is editing an existing incident but has made no changes.
      if (flooding) {
        unblockedNavigate(`/i/${flooding.id}`);
      }
      return;
    }

    const snackbarMessageKey = `createEditFlooding.successMessage.${
      flooding ? 'update' : 'create'
    }`;
    enqueueSnackbar(t(snackbarMessageKey), { variant: 'success' });

    unblockedNavigate(`/i/${mutatedGeoEvent.id}`);
  };

  const customEvacZoneStatuses = (
    hasStructuredEvacZones
      ? [
          customOrders && EvacZoneStatuses.orders,
          customWarnings && EvacZoneStatuses.warnings,
          customAdvisories && EvacZoneStatuses.advisories,
          customShelterInPlace && EvacZoneStatuses.shelterInPlace,
        ]
      : [
          removeHTMLTags(evacuationOrders) && EvacZoneStatuses.orders,
          removeHTMLTags(evacuationWarnings) && EvacZoneStatuses.warnings,
          removeHTMLTags(evacuationAdvisories) && EvacZoneStatuses.advisories,
          removeHTMLTags(evacuationShelterInPlace) &&
            EvacZoneStatuses.shelterInPlace,
        ]
  ).filter(Boolean) as EvacZoneStatus[];

  const handleRegionsChange = useRegionsChangeHandler({
    evacZoneStatuses,
    regionEvacZones,
    customEvacZoneStatuses,
    evacZoneStyles,
    unstructuredEvacuationRegionIds,
  });

  const isActive = status === 'active';

  const markerEditRegionSync = useMarkerEditRegionSync({
    geoEventType: GeoEventTypes.flooding,
    regionOptions,
    enabled: Boolean(flooding),
    handleRegionsChange,
    getValues,
    setValue,
  });

  const markerLock = useMarkerEditLock({
    onCommit: handleLocationChange,
    isEditing: Boolean(flooding),
    onEnterEditMode: markerEditRegionSync.onEnterEditMode,
    onStage: markerEditRegionSync.onStage,
    onCancel: markerEditRegionSync.onCancel,
  });

  const { checkbox: markerMoveCheckbox, isBlockingSubmit: markerMoveBlocking } =
    useMarkerMoveConfirmation({
      originalLatLng: flooding
        ? { lat: flooding.lat, lng: flooding.lng }
        : null,
      currentLatLng: { lat, lng },
      isEditing: Boolean(flooding),
    });

  const displayLat = markerLock.stagedLatLng?.lat ?? lat;
  const displayLng = markerLock.stagedLatLng?.lng ?? lng;

  // While the marker is being actively edited, clicks anywhere on the map reposition the marker
  const isMarkerEditActive = markerLock.enabled && !markerLock.isLocked;

  const currentGeoEvent = useMemo(() => {
    const geoEventWithFormValues: Partial<Flooding> = flooding || {};
    return {
      ...geoEventWithFormValues,
      name,
      data: geoEventWithFormValues.data || {},
      notificationType,
      isActive,
      lat: displayLat,
      lng: displayLng,
    } as Partial<Flooding>;
  }, [flooding, isActive, displayLat, displayLng, name, notificationType]);

  const hasFieldError = formState.errors.areaImpacted;
  const areaImpactedWarning = hasFieldError
    ? undefined
    : validateAreaImpacted(areaImpacted, t);

  const formFields = (
    <Stack spacing={2}>
      {!isInternalTabEnabled && (
        <Typography variant="h3">
          {flooding?.id
            ? t('createEditFlooding.title.edit')
            : t('createEditFlooding.title.add')}
        </Typography>
      )}

      <Controller
        name="name"
        control={control}
        render={({ field, fieldState }): JSX.Element => {
          const { ref, ...muiFieldProps } = field;
          return (
            <TextField
              id="field-control-name"
              label={t('createEditFlooding.inputs.name.label')}
              fullWidth
              {...muiFieldProps}
              inputRef={ref}
              required
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
              disabled={formState.isSubmitting}
            />
          );
        }}
      />

      <Controller
        name="regions"
        control={control}
        render={({ field, fieldState }): JSX.Element => {
          return (
            <FormControl fullWidth>
              <AutocompleteWithBackdrop
                multiple
                id="regions"
                groupBy={(region): string => region.state}
                options={regionOptions}
                getOptionLabel={(region): string =>
                  `${region.displayName}, ${region.state}`
                }
                isOptionEqualToValue={(option, value): boolean =>
                  option.id === value.id
                }
                value={field.value}
                onChange={(_e, newRegions): void => {
                  const validatedRegions = handleRegionsChange(
                    newRegions,
                    field.value,
                  );
                  field.onChange(validatedRegions);
                }}
                onBlur={field.onBlur}
                disabled={formState.isSubmitting}
                renderOption={(
                  optionProps,
                  region,
                  { selected },
                ): JSX.Element => {
                  const { key, ...otherProps } = optionProps;
                  return (
                    <li key={key} {...otherProps}>
                      <Checkbox
                        icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                        checkedIcon={<CheckBoxIcon fontSize="small" />}
                        sx={{ marginRight: 1 }}
                        checked={selected}
                      />
                      {region.displayName}
                    </li>
                  );
                }}
                renderInput={(params): JSX.Element => (
                  <TextField
                    {...params}
                    name={field.name}
                    inputRef={field.ref}
                    label={t('editGeoEvent.inputs.counties.label')}
                    error={Boolean(fieldState.error)}
                    helperText={fieldState.error?.message}
                    required
                  />
                )}
              />
            </FormControl>
          );
        }}
      />

      <Controller
        name="areaImpacted"
        control={control}
        render={({ field, fieldState }): JSX.Element => {
          const { ref, ...muiFieldProps } = field;
          return (
            <TextField
              id="field-control-areaImpacted"
              label={t('createEditFlooding.inputs.areaImpacted.label')}
              fullWidth
              {...muiFieldProps}
              inputRef={ref}
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message || areaImpactedWarning}
              disabled={formState.isSubmitting}
              color={areaImpactedWarning ? 'warning' : undefined}
              focused={Boolean(areaImpactedWarning)}
              slotProps={{
                formHelperText: {
                  sx: {
                    color: areaImpactedWarning ? 'warning.main' : undefined,
                  },
                },
              }}
            />
          );
        }}
      />

      <Controller
        name="status"
        control={control}
        render={({ field, fieldState }): JSX.Element => {
          const { ref, ...muiFieldProps } = field;
          return (
            <TextField
              select
              id="field-control-status"
              label={t('createEditFlooding.inputs.status.label')}
              fullWidth
              {...muiFieldProps}
              inputRef={ref}
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
              required
              disabled={formState.isSubmitting}
            >
              <MenuItem value="active">{t('geoEvent.active')}</MenuItem>
              <MenuItem value="inactive">{t('geoEvent.inactive')}</MenuItem>
            </TextField>
          );
        }}
      />

      <Controller
        name="notificationType"
        control={control}
        render={({ field, fieldState }): JSX.Element => {
          const { ref, ...muiFieldProps } = field;
          return (
            <TextField
              select
              id="field-control-notificationType"
              label={t('createEditFlooding.inputs.notificationType.label')}
              fullWidth
              {...muiFieldProps}
              inputRef={ref}
              error={Boolean(fieldState.error)}
              helperText={fieldState.error?.message}
              required
              className={classes.capitalizeLabel}
              disabled={
                formState.isSubmitting ||
                flooding?.notificationType === NotificationType.Normal
              }
            >
              {[
                NotificationTypeConstant.NORMAL,
                NotificationTypeConstant.SILENT,
              ].map((notifType) => (
                <MenuItem
                  key={notifType}
                  value={notifType}
                  className={classes.capitalizeLabel}
                >
                  {notifType}
                </MenuItem>
              ))}
            </TextField>
          );
        }}
      />

      <FloodingDeactivationAlert
        hasEvacZones={Boolean(regionEvacZones.length)}
      />

      {Boolean(flooding) && (
        <FloodingManualDeactivationField
          hasEvacZones={Boolean(regionEvacZones.length)}
        />
      )}

      {flooding?.data?.isParent && (
        <Controller
          name="childFloodings"
          control={control}
          render={({ field, fieldState }) => {
            return (
              <FormControl fullWidth>
                <AutocompleteWithBackdrop
                  {...geoEventAutocompleteProps}
                  value={field.value}
                  onChange={(_, value) => {
                    field.onChange(value);
                  }}
                  onBlur={field.onBlur}
                  disabled={formState.isSubmitting || !isFloodStaffReporter}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name={field.name}
                      inputRef={field.ref}
                      variant="outlined"
                      label={t(
                        'createEditFlooding.inputs.childFloodings.label',
                      )}
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </FormControl>
            );
          }}
        />
      )}

      {showParentField && isFloodStaffReporter && (
        <Controller
          name="parentFlooding"
          control={control}
          render={({ field, fieldState }) => {
            return (
              <FormControl fullWidth>
                <AutocompleteWithBackdrop
                  {...parentAutocompleteProps}
                  value={field.value}
                  onChange={(_, value) => {
                    field.onChange(value);
                  }}
                  onBlur={field.onBlur}
                  disabled={formState.isSubmitting}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      name={field.name}
                      inputRef={field.ref}
                      variant="outlined"
                      label={t(
                        'createEditFlooding.inputs.parentFlooding.label',
                      )}
                      error={Boolean(fieldState.error)}
                      helperText={fieldState.error?.message}
                    />
                  )}
                />
              </FormControl>
            );
          }}
        />
      )}

      {!isInternalTabEnabled && (
        <>
          <FormLinks />
          <Box sx={{ marginTop: 1 }}>
            <Typography variant="h3">
              {t('createEditFlooding.sections.internal')}
            </Typography>
          </Box>

          <UrgencySelector
            disabled={!isFloodStaffReporter || formState.isSubmitting}
          />

          <InternalNotesInput
            key={getIncidentRemountKey('internal-notes', flooding)}
            initialValue={defaultValues.reporterOnlyNotes}
          />

          <FormInternalLinks />
        </>
      )}

      {regions && regions.length > 0 && (
        <EvacuationFormSection
          key={getIncidentRemountKey('evacuations', flooding)}
          isExpanded={activeEvacuations}
          onToggle={() => setValue('activeEvacuations', !activeEvacuations)}
          isSubmitting={formState.isSubmitting}
          evacZones={regionEvacZones}
          zoneStyles={evacZoneStyles}
          isLoading={isLoadingEvacZones}
          isError={isErrorEvacZones}
        />
      )}

      {!flooding && (
        <>
          <Box sx={{ marginTop: 1 }}>
            <Typography variant="h3">
              {t('createEditFlooding.sections.update')}
            </Typography>
          </Box>

          <InitialReportNotificationTypeField
            label={t('createEditFlooding.inputs.reportNotificationType.label')}
            canSendCritical={canSendCriticalInitialReport}
          />

          <ReportMessageField
            name="reportMessage"
            initialValue={defaultValues.reportMessage}
            currentValues={{ notificationType }}
            helperText={t('createEditFlooding.inputs.reportMessage.helperText')}
            isCritical={isCriticalInitialReport}
          />

          <MediaForm
            latFieldName="mediaLat"
            lngFieldName="mediaLng"
            azFieldName="mediaAz"
            geoEventType={GeoEventTypes.flooding}
          />
        </>
      )}

      {isInternalTabEnabled && <FormLinks />}

      {showPreviewSection && (
        <Box sx={{ marginTop: 3 }}>
          <Typography variant="h3">
            {t('createEditFlooding.sections.preview')}
          </Typography>
        </Box>
      )}

      {showPreview && (
        <Box sx={{ marginTop: 2 }}>
          <PushNotificationPreview
            title={name}
            body={previewBody}
            isBodyLoading={isLoadingInitialReport}
            isBodyError={isErrorInitialReport}
            isCritical={isCriticalInitialReport}
          />
        </Box>
      )}

      {showNoNotificationNote && (
        <Box sx={{ marginTop: 3 }}>
          <Typography className={classes.noPushNotif}>
            {t('createEditFlooding.noPushNotification')}
          </Typography>
        </Box>
      )}

      {flooding && (
        <>
          <FloodingChangelog
            flooding={flooding}
            evacZones={regionEvacZones}
            zoneStyle={getEvacZoneStyleFromGeoEvent(flooding)}
          />

          <FloodingWarnings flooding={flooding} hazards={hazards} />
        </>
      )}

      {formState.errors.evacZoneStatuses?.message && (
        <PendingEvacZonesError
          zoneNames={formState.errors.evacZoneStatuses.message}
          type={
            formState.errors.evacZoneStatuses
              .type as PendingReviewEvacZoneErrorType
          }
        />
      )}

      {markerMoveCheckbox}

      {isCriticalInitialReport && (
        <CriticalAlertConfirmation
          messageFieldName="reportMessage"
          onConfirmationChange={setIsCriticalConfirmed}
        />
      )}

      <Box className={classes.lastItem}>
        <Button
          type="submit"
          fullWidth
          size="large"
          disabled={
            formState.isSubmitting ||
            (!flooding && uploadImgError) ||
            markerLock.hasStagedChange ||
            markerMoveBlocking ||
            (isCriticalInitialReport && !isCriticalConfirmed)
          }
        >
          {t(
            getSubmitButtonKey({
              isEditing: Boolean(flooding),
              isCritical: isCriticalInitialReport,
            }),
          )}
        </Button>
      </Box>
    </Stack>
  );

  return (
    <MapProvider>
      <MapFormLayout ref={scrollContainerRef}>
        <MapFormLayout.MapContent>
          <Map
            center={defaultMapCenter}
            zoom={initialZoom}
            mapType={MapType.FloodingForm}
            showSearch
            searchDisabled={markerLock.isLocked}
            searchBarAdornment={<MarkerEditControls lock={markerLock} />}
            cursor={markerLock.isLocked ? undefined : 'crosshair'}
            onLocationChange={markerLock.handleLocationChange}
            searchApi="google"
          >
            <FormExternalFeatureLayers />
            {activeEvacuations && renderMapLayers(isMarkerEditActive)}
            <AlertCamerasLayer
              visible
              interactive={false}
              withLabels
              selectedCameraId={
                isMediaAlertCamera(media) ? media.id : undefined
              }
            />
            <InactiveFloodingsLayer
              floodingEvents={floodingEvents}
              selectedGeoEventId={flooding?.id}
              interactive={false}
              isEdit
            />
            <WildfiresLayer
              isFadable
              wildfires={wildfireEvents}
              interactive={false}
            />
            <ActiveFloodingsLayer
              floodingEvents={floodingEvents}
              selectedGeoEventId={flooding?.id}
              interactive={false}
              isEdit
            />
            <FloodingMarker
              geoEvent={currentGeoEvent}
              onChange={markerLock.handleLocationChange}
              locked={markerLock.isLocked}
              moveOnEvacZoneClick={isMarkerEditActive}
            />
            {flooding && (
              <MarkerEditGhost
                markerLock={markerLock}
                original={flooding}
                footprint={iconFootprint(
                  getFloodingEventIcon(flooding),
                  'bottom',
                  getFloodingEventScale(flooding),
                )}
              >
                <Map.FloodingGeoEventMarker
                  geoEvent={flooding}
                  draggable={false}
                  opacityOverride={0.5}
                />
              </MarkerEditGhost>
            )}
          </Map>
        </MapFormLayout.MapContent>
        <MapFormLayout.FormContent disablePadding={isInternalTabEnabled}>
          <FormProvider {...formMethods}>
            <FormWithoutEnter onSubmit={handleSubmit(onSubmit)} noValidate>
              {isInternalTabEnabled ? (
                <GeoEventFormTabs<FloodingFormValues>
                  idPrefix="flooding-form"
                  internalFieldNames={[
                    'urgency',
                    'internalLinks',
                    'newReporterNote',
                  ]}
                  internalContent={
                    <InternalFormSection
                      key={getIncidentRemountKey('internal-form', flooding)}
                      geoEventId={flooding?.id}
                      newReporterNoteInitialValue={
                        defaultValues.newReporterNote
                      }
                      showUrgency
                      canEditUrgency={isFloodStaffReporter}
                      showInternalLinks
                    />
                  }
                >
                  {formFields}
                </GeoEventFormTabs>
              ) : (
                formFields
              )}
            </FormWithoutEnter>
          </FormProvider>
        </MapFormLayout.FormContent>
      </MapFormLayout>
    </MapProvider>
  );
};
